import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, ArrowDown, ArrowUp, ClipboardList, Loader2, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const emptyForm = { name: '', description: '', step_ids: [] };
const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export const LeadSources = () => {
  const perms = detectPerms();
  const [sources, setSources] = useState([]);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fetchSources = useCallback(async () => {
    try {
      setLoading(true);
      const [sourcesResponse, stepsResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/lead-sources`, authConfig()),
        axios.get(`${BACKEND_URL}/api/service-steps`, authConfig()),
      ]);
      setSources(sourcesResponse.data || []);
      setSteps(stepsResponse.data || []);
    } catch {
      setError('Failed to fetch lead sources');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSources(); }, [fetchSources]);

  const openCreate = () => {
    setForm({ ...emptyForm, step_ids: steps.map(step => step.id) });
    setEditing(null);
    setError('');
    setShowModal(true);
  };

  const openEdit = (source) => {
    const savedStepIds = (source.step_ids || []).filter(stepId => steps.some(step => step.id === stepId));
    setForm({
      name: source.name || '',
      description: source.description || '',
      step_ids: savedStepIds,
    });
    setEditing(source);
    setError('');
    setShowModal(true);
  };

  const saveSource = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }

    setSubmitting(true);
    setError('');
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      step_ids: form.step_ids,
    };
    try {
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/lead-sources/${editing.id}`, payload, authConfig());
      } else {
        await axios.post(`${BACKEND_URL}/api/lead-sources`, payload, authConfig());
      }
      setShowModal(false);
      await fetchSources();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save lead source');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteSource = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/lead-sources/${deleteTarget.id}`, authConfig());
      setDeleteTarget(null);
      await fetchSources();
    } catch (err) {
      setDeleteTarget(null);
      setError(err.response?.data?.detail || 'Failed to delete lead source');
    }
  };

  const filteredSources = sources.filter((source) => {
    const query = search.trim().toLowerCase();
    return !query
      || source.name?.toLowerCase().includes(query)
      || source.description?.toLowerCase().includes(query);
  });

  const moveStep = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= form.step_ids.length) return;
    setForm(current => {
      const stepIds = [...current.step_ids];
      [stepIds[index], stepIds[targetIndex]] = [stepIds[targetIndex], stepIds[index]];
      return { ...current, step_ids: stepIds };
    });
  };

  const removeStep = (stepId) => {
    setForm(current => ({
      ...current,
      step_ids: current.step_ids.filter(id => id !== stepId),
    }));
  };

  const addStep = (stepId) => {
    if (!stepId) return;
    setForm(current => current.step_ids.includes(stepId)
      ? current
      : { ...current, step_ids: [...current.step_ids, stepId] });
  };

  return (
    <div data-testid="lead-sources-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Lead Source</h1>
          <p className="text-slate-600 mt-1">Manage the sources used while creating leads</p>
        </div>
        {perms.canAdd && (
          <button type="button" onClick={openCreate} data-testid="add-lead-source-btn"
            className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
            <Plus className="h-5 w-5" /> Add Lead Source
          </button>
        )}
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input value={search} onChange={(event) => setSearch(event.target.value)}
            placeholder="Search lead sources..." data-testid="search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
        </div>
      </div>

      {error && !showModal && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
          <button type="button" onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
        ) : filteredSources.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No lead sources found</h3>
            <p className="text-slate-500">{sources.length ? 'Try adjusting your search' : 'Add a lead source before creating a lead'}</p>
          </div>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-7 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="col-span-2 text-sm font-semibold text-slate-700 uppercase tracking-wider">Name</div>
              <div className="col-span-3 text-sm font-semibold text-slate-700 uppercase tracking-wider">Description</div>
              <div className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Steps</div>
              <div className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Actions</div>
            </div>
            {filteredSources.map((source) => (
              <div key={source.id} data-testid={`lead-source-row-${source.id}`}
                className="grid grid-cols-1 md:grid-cols-7 gap-4 px-6 py-4 border-b border-slate-100 last:border-0 items-center hover:bg-slate-50">
                <div className="md:col-span-2 font-medium text-slate-900">{source.name}</div>
                <div className="md:col-span-3 text-sm text-slate-600">{source.description || '-'}</div>
                <div className="text-sm text-slate-600">{source.step_ids?.length || 0}</div>
                <div className="flex items-center gap-1">
                  {perms.canEdit && (
                    <button type="button" onClick={() => openEdit(source)} data-testid={`edit-btn-${source.id}`}
                      className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors">
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                  {perms.canDelete && (
                    <button type="button" onClick={() => setDeleteTarget(source)} data-testid={`delete-btn-${source.id}`}
                      className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">{editing ? 'Edit Lead Source' : 'Add Lead Source'}</h2>
              <button type="button" onClick={() => setShowModal(false)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={saveSource} className="p-6 overflow-y-auto flex-1">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-5">
                  <AlertCircle className="h-5 w-5" /><span>{error}</span>
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name <span className="text-red-500">*</span></label>
                  <input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                    required data-testid="input-name"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                    rows={3} data-testid="input-description"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none" />
                </div>
                <div>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <label className="block text-sm font-medium text-slate-700">Default Steps</label>
                    <span className="text-xs text-slate-500">Choose steps and arrange their opening order</span>
                  </div>
                  {steps.some(step => !form.step_ids.includes(step.id)) && (
                    <select value="" onChange={(event) => addStep(event.target.value)}
                      data-testid="add-lead-source-step"
                      className="w-full px-3 py-2 mb-3 border border-slate-200 rounded-sm bg-white focus:border-orange-500 focus:outline-none">
                      <option value="">Add a step...</option>
                      {steps.filter(step => !form.step_ids.includes(step.id)).map(step => (
                        <option key={step.id} value={step.id}>{step.name}</option>
                      ))}
                    </select>
                  )}
                  {form.step_ids.length === 0 ? (
                    <div className="border border-dashed border-slate-300 rounded-sm px-4 py-6 text-center">
                      <ClipboardList className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm text-slate-500">No default steps selected for this lead source.</p>
                    </div>
                  ) : (
                    <div className="border border-slate-200 rounded-sm divide-y divide-slate-100" data-testid="lead-source-step-order">
                      {form.step_ids.map((stepId, index) => {
                        const step = steps.find(item => item.id === stepId);
                        if (!step) return null;
                        return (
                          <div key={stepId} className="flex items-center gap-3 px-3 py-2.5">
                            <span className="h-7 w-7 shrink-0 rounded-full bg-orange-50 text-orange-700 text-xs font-bold flex items-center justify-center">
                              {index + 1}
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-slate-900">{step.name}</p>
                              {step.description && <p className="text-xs text-slate-500 truncate">{step.description}</p>}
                            </div>
                            <div className="flex items-center gap-1">
                              <button type="button" onClick={() => moveStep(index, -1)} disabled={index === 0}
                                aria-label={`Move ${step.name} up`} data-testid={`move-step-up-${stepId}`}
                                className="p-1.5 border border-slate-200 rounded-sm text-slate-500 hover:text-orange-600 hover:bg-orange-50 disabled:opacity-30 disabled:hover:bg-white">
                                <ArrowUp className="h-4 w-4" />
                              </button>
                              <button type="button" onClick={() => moveStep(index, 1)} disabled={index === form.step_ids.length - 1}
                                aria-label={`Move ${step.name} down`} data-testid={`move-step-down-${stepId}`}
                                className="p-1.5 border border-slate-200 rounded-sm text-slate-500 hover:text-orange-600 hover:bg-orange-50 disabled:opacity-30 disabled:hover:bg-white">
                                <ArrowDown className="h-4 w-4" />
                              </button>
                              <button type="button" onClick={() => removeStep(stepId)}
                                aria-label={`Remove ${step.name}`} data-testid={`remove-step-${stepId}`}
                                className="p-1.5 border border-red-200 rounded-sm text-red-500 hover:text-red-600 hover:bg-red-50">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-slate-200">
                <button type="button" onClick={() => setShowModal(false)} className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="submit-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}{editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-3">Delete Lead Source?</h3>
            <p className="text-slate-600 mb-6">Delete “{deleteTarget.name}”? Sources already used by leads cannot be deleted.</p>
            <div className="flex gap-3 justify-end">
              <button type="button" onClick={() => setDeleteTarget(null)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={deleteSource} data-testid="confirm-delete-btn" className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
